import { type ComponentType, lazy, ReactNode, Suspense } from 'react';
import {
	createBrowserRouter,
	Navigate,
	Outlet,
	type RouteObject,
	RouterProvider as DomRouterProvider,
} from 'react-router-dom';

import { ROUTES } from '@shared/config/routes';
import { PageLoader } from '@shared/ui/page-loader';
import { useAuthGuard } from '@features/auth';
import { Layout } from '@widgets/layout';

const CallPage = lazy(() => import('@pages/call-page'));
const LoginPage = lazy(() => import('@pages/login-page'));

// Компонент для ленивой загрузки страниц
const LazyPage = ({ children }: { children: ReactNode }) => (
	<Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
	const { isChecking } = useAuthGuard();

	return isChecking ? <PageLoader /> : children;
};

const PublicRoute = ({ children }: { children: ReactNode }) => children;

const LayoutShell = () => (
	<Layout>
		<Outlet />
	</Layout>
);

const renderProtectedRoute = (Component: ComponentType) => (
	<ProtectedRoute>
		<LazyPage>
			<Component />
		</LazyPage>
	</ProtectedRoute>
);

const renderPublicRoute = (Component: ComponentType) => (
	<PublicRoute>
		<LazyPage>
			<Component />
		</LazyPage>
	</PublicRoute>
);

const childPath = (path: string) => (path.startsWith('/') ? path.slice(1) : path);

const routes: RouteObject[] = [
	{
		path: ROUTES.HOME,
		element: <LayoutShell />,
		children: [
			{ index: true, element: renderProtectedRoute(CallPage) },
			{ path: childPath(ROUTES.CALL), element: renderProtectedRoute(CallPage) },
			{ path: childPath(ROUTES.CALL_ROOM), element: renderProtectedRoute(CallPage) },
			{
				path: childPath(ROUTES.LOGIN),
				element: renderPublicRoute(LoginPage),
			},
			{ path: '*', element: <Navigate replace to={ROUTES.HOME} /> },
		],
	},
];

const router = createBrowserRouter(routes, { basename: '/' });

export const RouterProvider = () => <DomRouterProvider router={router} />;
